import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBrandTheme } from '@/contexts/BrandThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Palette, Check, Users, Baby, Building2, Cross, Crown, Globe } from 'lucide-react';

const AUDIENCE_META = {
  parents: { label: 'Parents (individual)', icon: Users, desc: 'Direct-to-consumer parents using Zazu at home' },
  kids: { label: 'Kids / Students', icon: Baby, desc: 'Child-facing learning experience' },
  org_admin: { label: 'Organizations & Schools', icon: Building2, desc: 'Org admins, teachers, and org students' },
  christian: { label: 'Christian Subscribers', icon: Cross, desc: 'Users on Christian education plans' },
  platform_admin: { label: 'Platform Admin (yourselves)', icon: Crown, desc: 'Your own admin console look' },
  default: { label: 'Default / Visitors', icon: Globe, desc: 'Landing page and unauthenticated visitors' },
};

const PlatformAdminThemes = () => {
  const { api } = useAuth();
  const { refresh } = useBrandTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/themes/assignments');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load themes');
    } finally {
      setLoading(false);
    }
  };

  const assign = async (audience, themeKey) => {
    setSaving(`${audience}:${themeKey}`);
    try {
      await api.put('/admin/themes/assignments', { audience, theme_key: themeKey });
      setData((prev) => ({ ...prev, assignments: { ...prev.assignments, [audience]: themeKey } }));
      toast.success(`${AUDIENCE_META[audience].label} → ${data.themes.find((t) => t.key === themeKey)?.name}`);
      // If platform_admin theme changed, refresh our own theme right away
      if (audience === 'platform_admin' || audience === 'default') {
        await refresh();
      }
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setSaving(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600 mx-auto" /></div>
    );
  }

  const calmThemes = data.themes.filter((t) => t.kind === 'calm');
  const playfulThemes = data.themes.filter((t) => t.kind === 'playful');

  return (
    <div className="space-y-6" data-testid="admin-themes-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-teal-600" /> Branding & Themes
          </CardTitle>
          <CardDescription>
            Choose a color theme for each audience. Changes apply instantly the next time a user from that audience visits.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <ThemeShowcase title="Calm (Professional)" themes={calmThemes} />
            <ThemeShowcase title="Playful (Vibrant)" themes={playfulThemes} />
          </div>

          <div className="space-y-4 mt-8">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Audience Assignments</h3>
            {Object.entries(AUDIENCE_META).map(([audKey, meta]) => {
              const Icon = meta.icon;
              const currentKey = data.assignments[audKey];
              return (
                <div key={audKey} className="border border-slate-200 rounded-2xl p-5 bg-white" data-testid={`audience-${audKey}`}>
                  <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-slate-700" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{meta.label}</h4>
                        <p className="text-xs text-slate-500">{meta.desc}</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-2">
                    {data.themes.map((t) => {
                      const selected = currentKey === t.key;
                      const isSaving = saving === `${audKey}:${t.key}`;
                      return (
                        <button
                          key={t.key}
                          onClick={() => assign(audKey, t.key)}
                          disabled={isSaving}
                          className={`relative p-2 rounded-xl border-2 transition-all hover:scale-105 ${selected ? 'border-teal-600 ring-2 ring-teal-200' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid={`theme-pick-${audKey}-${t.key}`}
                          title={t.name}
                        >
                          <div
                            className="w-full h-12 rounded-lg shadow-inner"
                            style={{ backgroundColor: t.swatch }}
                          />
                          <p className="text-[10px] text-slate-700 mt-1.5 font-medium truncate">{t.name}</p>
                          {selected && (
                            <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-teal-600 flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                          {isSaving && (
                            <div className="absolute inset-0 bg-white/70 rounded-xl flex items-center justify-center">
                              <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ThemeShowcase = ({ title, themes }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{title}</h4>
    <div className="grid grid-cols-3 gap-2">
      {themes.map((t) => (
        <div key={t.key} className="text-center">
          <div className="w-full aspect-square rounded-xl shadow-sm" style={{ backgroundColor: t.swatch }} />
          <p className="text-xs text-slate-700 mt-1.5 font-medium">{t.name}</p>
        </div>
      ))}
    </div>
  </div>
);

export default PlatformAdminThemes;
